/**
 * useSectionContextMenu — Rename / Remove an organ-subsection label, shared
 * by BOTH the case-viewer report editor (PrototypeReport) and the template
 * editor (TemplateEditor). Click or keyboard-activate a heading to open the
 * menu; right-click remains a shortcut to the same actions.
 *
 * Rename / remove edit THIS editor's doc — per-report in the case viewer,
 * per-template in the template editor — via the shared extensions helpers.
 * Returns the menu element to render (or null).
 */

import { useCallback, useEffect, useState, type ReactElement } from "react";
import type { Editor } from "@tiptap/react";
import { closeHistory } from "@tiptap/pm/history";
import { SectionContextMenu } from "@/components/reporter/SectionContextMenu";
import {
  buildSectionRemoveConfirm,
  findOrganSubsectionByKind,
  renameOrganSubsectionLabel,
} from "@/components/tiptap/extensions";

interface SectionMenuState {
  kind: string;
  label: string;
  anchor: { x: number; y: number };
}

function useSectionMenuTrigger(
  editor: Editor | null,
  menu: SectionMenuState | null,
  setMenu: (menu: SectionMenuState | null) => void,
) {
  useEffect(() => {
    if (!editor?.view?.dom) return;
    const dom = editor.view.dom;
    const open = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const labelEl = target.closest<HTMLButtonElement>(".organ-subsection__label");
      if (!labelEl || !editor.isEditable) return;
      const sectionEl = labelEl.closest(".organ-subsection");
      const kind = sectionEl?.getAttribute("data-kind");
      if (!kind) return;
      event.preventDefault();
      event.stopPropagation();
      labelEl.focus();
      const rect = labelEl.getBoundingClientRect();
      setMenu({
        kind,
        label: sectionEl?.getAttribute("data-label") ?? "",
        anchor:
          event.type === "contextmenu"
            ? { x: event.clientX, y: event.clientY }
            : { x: rect.left, y: rect.bottom },
      });
    };
    dom.addEventListener("click", open);
    dom.addEventListener("contextmenu", open);
    return () => {
      dom.removeEventListener("click", open);
      dom.removeEventListener("contextmenu", open);
    };
  }, [editor, setMenu]);

  useEffect(() => {
    if (!editor) return;
    const sync = () => {
      const labels = editor.view.dom.querySelectorAll<HTMLButtonElement>(
        ".organ-subsection__label",
      );
      labels.forEach((label) => {
        label.disabled = !editor.isEditable;
        const kind = label.closest(".organ-subsection")?.getAttribute("data-kind");
        label.setAttribute("aria-expanded", String(editor.isEditable && kind === menu?.kind));
      });
      if (!editor.isEditable && menu) setMenu(null);
    };
    sync();
    editor.on("update", sync);
    return () => {
      editor.off("update", sync);
    };
  }, [editor, menu, setMenu]);
}

export function useSectionContextMenu(editor: Editor | null): ReactElement | null {
  const [menu, setMenu] = useState<SectionMenuState | null>(null);
  const close = useCallback(() => setMenu(null), []);
  useSectionMenuTrigger(editor, menu, setMenu);

  const handleRename = useCallback(
    (kind: string) => {
      if (!editor || !editor.isEditable) return;
      const found = findOrganSubsectionByKind(editor.state.doc, kind);
      if (!found) return;
      const current = (found.node.attrs.label as string | undefined) ?? "";
      const raw = window.prompt("Rename section", current);
      const next = raw?.trim() ?? "";
      if (!next || next === current) {
        editor.view.focus();
        return;
      }
      renameOrganSubsectionLabel(editor.view, kind, next);
      editor.view.focus();
    },
    [editor],
  );

  const handleRemove = useCallback(
    (kind: string) => {
      if (!editor || !editor.isEditable) return;
      const found = findOrganSubsectionByKind(editor.state.doc, kind);
      if (!found) return;
      if (!window.confirm(buildSectionRemoveConfirm(found.node))) return;
      const { view } = editor;
      // Undo must restore the section as it was immediately before removal,
      // without also undoing a recent rename, finding acceptance or text edit.
      view.dispatch(closeHistory(view.state.tr.delete(found.pos, found.pos + found.node.nodeSize)));
      view.focus();
    },
    [editor],
  );

  if (!menu) return null;
  return (
    <SectionContextMenu
      anchor={menu.anchor}
      label={menu.label}
      onRename={() => handleRename(menu.kind)}
      onRemove={() => handleRemove(menu.kind)}
      onClose={close}
    />
  );
}
